import { useEffect, useState } from 'react';
import { User } from '@core/generated/gql-types';
import { useOnboardingStore } from '@core/onboarding/useOnboardingStore';
import { Spinner } from '@/components/shared/Spinner';

const OnboardingProgress = ({ worker }: { worker: User }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { getOnboardingForUser, fetchUserOnboarding } = useOnboardingStore();

  const onboardingForUser = getOnboardingForUser(worker.id);

  useEffect(() => {
    setIsLoading(true);
    fetchUserOnboarding(worker.id).finally(() => {
      setIsLoading(false);
    });
  }, [worker.id, fetchUserOnboarding]);

  const pages = onboardingForUser?.onboardingJSON.pages as Array<{
    id: string;
    title: string;
    key: string;
  }>;

  const currentPageId = Number(onboardingForUser?.currentPageId) || 0;
  const totalSteps = pages?.length || 0;
  const isOnboardingComplete = currentPageId === totalSteps;
  const progressPercentage = Math.round(worker.onboardingPercentage ?? 0);

  const getStepStatus = (pageId: number) => {
    const id = Number(pageId);
    if (id < currentPageId) {
      return 'completed';
    } else if (id === totalSteps && currentPageId === totalSteps) {
      return 'completed';
    } else if (id === currentPageId) {
      return 'active';
    } else {
      return 'pending';
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold">Onboarding Progress</h2>
      {!onboardingForUser?.onboardingJSON?.pages ? (
        <>{isLoading ? <Spinner /> : <p>No onboarding data found</p>}</>
      ) : (
        <>
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="relative h-2 w-full rounded-full bg-gray-200">
              <div
                className={`absolute top-0 left-0 h-full rounded-full transition-all duration-300 ${
                  isOnboardingComplete
                    ? 'bg-green-500'
                    : 'bg-[var(--action-color)]'
                }`}
                style={{ width: `${Math.max(progressPercentage, 1)}%` }}
              />
            </div>
            <div className="mt-2 flex justify-end">
              <span
                className={`text-sm font-medium ${
                  isOnboardingComplete
                    ? 'text-green-500'
                    : 'text-[var(--action-color)]'
                }`}
              >
                {progressPercentage}%
              </span>
            </div>
          </div>
          {/* Steps Grid - hide when onboarding is complete */}
          {!isOnboardingComplete && (
            <div className="grid grid-cols-5 gap-4">
              {pages.map((page, index) => {
                const stepNumber = index + 1;
                const status = getStepStatus(Number(page.id));

                return (
                  <div
                    key={page.id}
                    className={`flex items-center gap-2 ${
                      status === 'active'
                        ? 'text-[var(--action-color)]'
                        : 'text-gray-500'
                    }`}
                  >
                    {/* Step Circle */}
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                        status === 'completed'
                          ? 'bg-green-500 text-white'
                          : status === 'active'
                            ? 'bg-[var(--action-color)] text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {status === 'completed' ? (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        stepNumber
                      )}
                    </div>

                    {/* Step Title */}
                    <span
                      className={`min-w-0 text-sm font-medium whitespace-nowrap ${
                        status === 'active'
                          ? 'text-[var(--action-color)]'
                          : status === 'completed'
                            ? 'text-gray-900'
                            : 'text-gray-500'
                      } overflow-hidden text-ellipsis hover:overflow-visible hover:whitespace-normal`}
                    >
                      {page.title}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OnboardingProgress;
